// src/components/ThemeSwitch.tsx

"use client"

import * as RadioGroupPrimitives from "@radix-ui/react-radio-group"
import RiComputerLine from "~/icons/remix/generated/RiComputerLine"
import RiMoonLine from "~/icons/remix/generated/RiMoonLine"
import RiSunLine from "~/icons/remix/generated/RiSunLine"
import { usePathname } from "next/navigation"
import { useTheme } from "next-themes"
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ElementType,
  forwardRef,
  type ReactNode,
  useEffect,
  useState
} from "react"

import { sendEvent } from "~/lib/gtag"
import { cx, focusRing } from "~/lib/utils"

// Based on Tremor Raw RadioGroup [v0.0.0]

const RadioGroup = forwardRef<
  ElementRef<typeof RadioGroupPrimitives.Root>,
  ComponentPropsWithoutRef<typeof RadioGroupPrimitives.Root>
>(({ className, ...props }, forwardedRef) => {
  return (
    <RadioGroupPrimitives.Root
      ref={forwardedRef}
      className={cx("grid gap-2", className)}
      {...props}
    />
  )
})
RadioGroup.displayName = "RadioGroup"

const RadioGroupItem = forwardRef<
  ElementRef<typeof RadioGroupPrimitives.Item>,
  ComponentPropsWithoutRef<typeof RadioGroupPrimitives.Item> & {
    icon: ElementType
  }
>(({ className, icon, ...props }, forwardedRef) => {
  const Icon = icon
  return (
    <RadioGroupPrimitives.Item
      ref={forwardedRef}
      className={cx(
        "group relative flex size-8 appearance-none items-center justify-center outline-none",
        className
      )}
      {...props}>
      <div
        className={cx(
          // base
          "flex size-full shrink-0 items-center justify-center rounded-lg text-gray-700 dark:text-gray-400",
          // background color
          "bg-transparent",
          // checked
          "group-data-[state=checked]:bg-indigo-50 group-data-[state=checked]:text-indigo-600 dark:group-data-[state=checked]:bg-indigo-500/20 dark:group-data-[state=checked]:text-indigo-300",
          // focus
          ...focusRing
        )}>
        <Icon className="size-4 text-inherit" />
      </div>
    </RadioGroupPrimitives.Item>
  )
})
RadioGroupItem.displayName = "RadioGroupItem"

export { RadioGroup, RadioGroupItem }

const ThemeSwitch = (): ReactNode => {
  const [mounted, setMounted] = useState(false)
  const { theme, setTheme } = useTheme()
  const pathname = usePathname()

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot SSR hydration flag, set once after mount (client-only)
    setMounted(true)
  }, [])

  if (!mounted) {
    return null
  }

  return (
    <RadioGroup
      value={theme}
      onValueChange={(value) => {
        setTheme(value)
        sendEvent({
          action: "theme_change",
          label: `Theme changed to ${value}`,
          page_location: pathname
        })
      }}
      className="flex gap-1">
      <RadioGroupItem
        aria-label="Switch to System Mode"
        icon={RiComputerLine}
        value="system"
        id="system"
      />

      <RadioGroupItem
        aria-label="Switch to Light Mode"
        icon={RiSunLine}
        value="light"
        id="light"
      />

      <RadioGroupItem
        aria-label="Switch to Dark Mode"
        icon={RiMoonLine}
        value="dark"
        id="dark"
      />
    </RadioGroup>
  )
}

export default ThemeSwitch
